"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PortTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const useSortedData_1 = require("../../../../hooks/useSortedData");
const Modal_1 = require("../Modal");
const PortsSearchInput_1 = require("./PortsSearchInput");
const apiUtils_1 = require("../../../../util/apiUtils");
const lodash_1 = require("lodash");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const useFetch_1 = __importDefault(require("../../../../hooks/useFetch"));
const ExportCsvButton_1 = require("../ExportCsvButton");
const getColumns = (isInAdminTools) => [
    { key: "id", label: "Port ID", sortableKey: "id" },
    { key: "name", label: "Port name", sortableKey: "name" },
    { key: "un_code", label: "UN/LOCODE", sortableKey: "un_code" },
    { key: "country", label: "Country", sortableKey: "country" },
    { key: "latitude", label: "Latitude", sortableKey: "latitude" },
    { key: "longitude", label: "Longitude", sortableKey: "longitude" },
    { key: "port_region", label: "Region", sortableKey: "port_region" },
    { key: "region_code", label: "Region code", sortableKey: "region_code" },
    { key: "tools", label: "Tools" },
].filter((c) => c.key !== "tools" || isInAdminTools);
const PortCellContent = ({ port, contentKey, editPort, }) => {
    switch (contentKey) {
        case "name":
            return (0, jsx_runtime_1.jsx)("div", { children: port.name });
        case "tools":
            return editPort ? ((0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => editPort(port), children: "Edit" }) })) : ((0, jsx_runtime_1.jsx)("div", {}));
        default:
            return (0, jsx_runtime_1.jsx)("div", { children: port[contentKey] });
    }
};
const PortTable = ({ isInAdminTools }) => {
    const [ports, setUrl, setBody] = (0, useFetch_1.default)(undefined, undefined);
    // Memoize data to prevent infinite loop in useSortedData
    const dataForSorting = (0, react_1.useMemo)(() => ports.status === apiUtils_1.LoaderStatus.Success && ports.data ? ports.data : [], [ports.status]);
    // Prepare sorted data consistent with PortTable pattern
    const { sortedData, sortMethod, sortDirection, setSort } = (0, useSortedData_1.useSortedData)(dataForSorting, "name", 1);
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/port.json");
    const [query, setQuery] = (0, react_1.useState)("");
    const [displayPorts, setDisplayPorts] = (0, react_1.useState)(undefined);
    const [searchError, setSearchError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        const trimmed = query.trim();
        const isNumeric = /^\d+$/.test(trimmed);
        const canSearch = trimmed.length >= 3 || isNumeric;
        if (!canSearch) {
            setDisplayPorts(undefined);
            setSearchError(null);
            return;
        }
        const timeoutFunction = setTimeout(() => {
            setSearchError(null);
            setBody(JSON.stringify({
                search: query,
            }));
            setUrl("admin/ports_search.json");
        }, 300);
        return () => clearTimeout(timeoutFunction);
    }, [query, setUrl]);
    (0, react_1.useEffect)(() => {
        if (ports.status === apiUtils_1.LoaderStatus.Success && ports.data) {
            setSearchError(null);
            setDisplayPorts(ports.data);
        }
        else if (ports.status === apiUtils_1.LoaderStatus.Error) {
            setSearchError("Search failed; please try again.");
            setDisplayPorts(undefined);
        }
    }, [ports.status]);
    const [modal, setModal] = (0, react_1.useState)({});
    const addPort = () => {
        setModal({
            type: "add",
            value: {},
        });
    };
    const editPort = (port) => {
        setModal({
            type: "edit",
            value: port,
        });
    };
    const submit = async (type, value) => {
        const portInput = (0, lodash_1.pick)(value, compliance_pool_types_1.portInputKeys);
        if (type === "add" && isInAdminTools) {
            if ((0, compliance_pool_types_1.isValidPortInput)(portInput)) {
                const data = {
                    ...portInput,
                    id: undefined,
                };
                await executeEdit(data, "PUT");
            }
            else {
                alert("All fields are required: name, UN/LOCODE, country, latitude, longitude, port region, region code.");
            }
        }
        if (type === "edit" && isInAdminTools) {
            if ((0, compliance_pool_types_1.isValidPortInputEdit)(portInput)) {
                const data = {
                    ...portInput,
                    id: portInput.id,
                };
                await executeEdit(data, "PUT");
            }
            else {
                alert("All fields are required: name, country, latitude, longitude, port region, region code.");
            }
        }
    };
    (0, react_1.useEffect)(() => {
        if (editStatus.status === apiUtils_1.LoaderStatus.Success) {
            const updated = editStatus.data;
            if (updated) {
                setDisplayPorts((previousPortData) => {
                    if (!previousPortData) {
                        return undefined;
                    }
                    const findIndex = previousPortData.findIndex((p) => p.id === updated.id);
                    if (findIndex >= 0) {
                        // Port already exists
                        // Update existing port
                        const copy = previousPortData.slice();
                        copy[findIndex] = { ...previousPortData[findIndex], ...updated };
                        return copy;
                    }
                    // Add new port
                    return [...previousPortData, updated];
                });
            }
            // Close modal
            setModal({});
        }
        else if (editStatus.status === apiUtils_1.LoaderStatus.Error) {
            const message = (typeof editStatus.error === "string" && editStatus.error) ||
                (editStatus.error && editStatus.error.message) ||
                "Failed to save the port. Please check the values and try again.";
            alert(message);
        }
    }, [editStatus.status]);
    const columns = getColumns(isInAdminTools);
    const csvColumns = columns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (port) => {
            const value = port[String(column.key)];
            return typeof value === "string" || typeof value === "number"
                ? value
                : "";
        },
    }));
    return ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.TitleWithExtraContent, { titleLevel: "Section", extraContent: (0, jsx_runtime_1.jsxs)(common_styled_components_1.Row, { gap: 1, children: [isInAdminTools && (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { onClick: addPort, children: "+ Add" }), (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: sortedData, columns: csvColumns, tableName: "ports" })] }), children: "Ports" }), (0, jsx_runtime_1.jsx)(PortsSearchInput_1.PortsSearchInput, { query: query, onQueryChange: setQuery, ports: displayPorts, loading: ports.status === apiUtils_1.LoaderStatus.Loading, error: searchError, title: "Search ports by name, Code or ID" }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((c) => c.sortableKey ? ((0, jsx_runtime_1.jsx)(common_styled_components_1.SortableTH, { sortKey: c.sortableKey, sortDirection: sortMethod === c.sortableKey ? sortDirection : undefined, setSort: setSort, children: c.label }, c.key)) : ((0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: sortedData.map((p) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(PortCellContent, { contentKey: c.key, port: p, editPort: isInAdminTools ? editPort : undefined }) }, c.key))) }, p.id))) })] }), isInAdminTools && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "port", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, pending: editStatus.status === apiUtils_1.LoaderStatus.Loading, submit: submit }))] }));
};
exports.PortTable = PortTable;
